import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import { PatientMedicine } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { Modal, ModalCancelButton, ModalCloseButton, ModalWidth } from '@dreryk/react-components';

import { ExtendedDosage } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { DraftStandardPrescriptionFieldName } from '../../draftPrescriptions/standardPrescription/DraftStandardPrescriptionField.types';

import { StyledExtendedDosageFormContainer, StyledExtendedDosageHeaderSeparator } from './ExtendedDosage.styled';
import { ExtendedDosageForm } from './form/ExtendedDosageForm';
import { ExtendedDosageModalTitle } from './ExtendedDosageHeaderActionsPortal';
import { ExtendedDosageMedicineHeader } from './ExtendedDosageMedicineHeader';
import { ExtendedDosageModalConfirmButton, ExtendedDosageRemoveButton } from './ExtendedDosageModal.buttons';
import { ExtendedDosageTemplateActions } from './ExtendedDosageTemplateActions';
import { createExtendedDosageFormBeanInitializer, getExtendedDosagePortionUnit } from './extendedDosage.utils';

type ExtendedDosageModalProps = Pick<DraftPrescriptionsFormController, 'setValue'> & {
   readonly name: DraftStandardPrescriptionFieldName;
   readonly dosageFieldName: DraftStandardPrescriptionFieldName;
   readonly dosage: string | null;
   readonly extendedDosage: ExtendedDosage | null;
   readonly medicine: MedicineSimple;
   readonly reimbursements: PatientMedicine['reimbursements'];
   readonly dosageFrequencyValidated: boolean;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly onClose: VoidFunction;
};

export const ExtendedDosageModal = (props: ExtendedDosageModalProps): JSX.Element => {
   const portionUnit = getExtendedDosagePortionUnit(props.extendedDosage, props.medicine);

   return (
      <Modal
         title={<ExtendedDosageModalTitle title={prescriptionTranslationKeys.dosage.extendedDosage} />}
         onCloseRequest={props.onClose}
         animateVisibility
         width={ModalWidth.WIDE}
         testId={'extendedDosage'}
         shouldCloseOnEsc={false}
         shouldCloseOnOverlayClick={false}
         visible
      >
         <ExtendedDosageMedicineHeader
            medicine={props.medicine}
            reimbursements={props.reimbursements}
            visitDiagnoses={props.visitDiagnoses}
         />
         <StyledExtendedDosageHeaderSeparator />
         <StyledExtendedDosageFormContainer>
            <ExtendedDosageForm
               initializer={createExtendedDosageFormBeanInitializer(props.extendedDosage, portionUnit, props.dosage)}
               portionUnit={portionUnit}
               context={{
                  mode: 'PRESCRIPTION',
                  medicine: props.medicine,
                  dosageFrequencyValidated: props.dosageFrequencyValidated,
               }}
            >
               {({ handleSubmit, formState, saveBlocked, getTemplateExtendedDosage, applyTemplateExtendedDosage }) => (
                  <>
                     <ExtendedDosageTemplateActions
                        getTemplateExtendedDosage={getTemplateExtendedDosage}
                        applyTemplateExtendedDosage={applyTemplateExtendedDosage}
                        shouldConfirmUse={props.extendedDosage !== null || formState.isDirty}
                        saveTemplateDisabled={saveBlocked}
                     />
                     <ExtendedDosageRemoveButton setValue={props.setValue} name={props.name} onClose={props.onClose} />
                     <ExtendedDosageModalConfirmButton
                        handleSubmit={handleSubmit}
                        setValue={props.setValue}
                        name={props.name}
                        dosageFieldName={props.dosageFieldName}
                        saveBlocked={saveBlocked}
                        onClose={props.onClose}
                     />
                     <ModalCancelButton onClick={props.onClose} shouldConfirmExit={formState.isDirty} />
                     <ModalCloseButton onClick={props.onClose} shouldConfirmExit={formState.isDirty} />
                  </>
               )}
            </ExtendedDosageForm>
         </StyledExtendedDosageFormContainer>
      </Modal>
   );
};
